import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action } from '@ngrx/store';

import { createReducer, on } from '@ordersnproducts/utils/create-reducer';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import OrdersFormsActions from '../actions/orders-forms.action';
import { OrderULResponse } from '@cemex-core/cmx-api';
import OrderScheduleActions from '../actions/schedule-order-confirmation.actions';
import { OrderStrategyResponse } from '@ordersnproducts/modules/order-builder/models/order-strategy-response';
import { BusinessDayType } from '@ordersnproducts/utils/utils';
import { FormattedAddress } from '@ordersnproducts/modules/shared/models/FormattedAddress.interface';

export interface KeyValues {
  [key: string]: string | number;
}

export interface OrderForm {
  id: string;
  originalTimestamp: number;
  counter: number;
  values: KeyValues;
  valid: boolean;
  invalid: boolean;
  index?: number;
  // order creation
  orderDraft: OrderULResponse;
  orderConfirmation: OrderStrategyResponse;
  // dco
  waitingList?: boolean;
  deliveryDate?: string;
  waitingListDate?: string;
  requestedTimestamp?: number;
  // cards
  shown: boolean;
  shownTouched: boolean;
  // terms and conditions checkbox
  termsAndConditions: boolean;
  isBIGO?: boolean;
  businessDayType?: BusinessDayType;
  /**
   * This is a flag to indicate if the user has checked the liability checkbox
   */
  liabilityCheck?: boolean;
  duplicated: boolean;
  selectedPodAddress?: FormattedAddress;
}

export interface State extends EntityState<OrderForm> {
  selectedOrderFormId: string;
  latestDuplicatedOrdersFormsIds: string[];
  maximumNumberOfOrders: number;
}

export interface AdditionalInfoOrderConfirmation {
  waitingList?: boolean;
  deliveryDate?: string;
  waitingListDate?: string;
  requestedTimestamp?: number;
}

export const adapter: EntityAdapter<OrderForm> = createEntityAdapter<OrderForm>({
  selectId: (_orderForm) => _orderForm.id,
  sortComparer: false,
});

export let formCounter = -1;

const initialState: State = adapter.getInitialState({
  selectedOrderFormId: null,
  latestDuplicatedOrdersFormsIds: [],
  maximumNumberOfOrders: 20,
  shown: true,
  shownTouched: false,
});

export const ordersFormsReducer = createReducer(
  initialState,

  on(OrdersFormsActions.addForm, (state, { timestamp, defaultValues }) => {
    formCounter++;
    const entityId = timestamp + '/' + formCounter;
    defaultValues[SpecFieldNames.ID] = entityId;
    return adapter.addOne(
      {
        originalTimestamp: timestamp,
        values: defaultValues,
        counter: formCounter,
        id: entityId,
        valid: false,
        invalid: true,
        orderDraft: null,
        orderConfirmation: null,
        requestedTimestamp: timestamp,
        shown: true,
        shownTouched: false,
        termsAndConditions: false,
        duplicated: false,
      },
      state,
    );
  }),

  on(OrdersFormsActions.closeUntouchedForms, (state) => {
    const updateExisting = Object.keys(state.entities)
      .map((key) => {
        if (!state.entities[key].shownTouched) {
          return {
            id: state.entities[key].id,
            changes: {
              shown: false,
            },
          };
        } else {
          return null;
        }
      })
      .filter((ent) => !!ent);
    if (updateExisting.length > 0) {
      return adapter.updateMany(updateExisting, state);
    } else {
      return state;
    }
  }),

  on(OrdersFormsActions.updateForm, (state, { formId, formControlName, formControlvalue, formIsValid }) => {
    const entity = state.entities[formId];
    if (entity) {
      const updatedValues = entity.values;
      updatedValues[formControlName] = formControlvalue;
      return adapter.updateOne(
        {
          id: formId,
          changes: {
            values: updatedValues,
            valid: formIsValid,
            invalid: !formIsValid,
          },
        },
        state,
      );
    }
    return state;
  }),

  on(OrdersFormsActions.selectForm, (state, { formId }) => {
    return {
      ...state,
      selectedOrderFormId: formId,
    };
  }),

  on(OrdersFormsActions.updateLimitOfOrderForm, (state, { maximumNumberOfOrders }) => {
    return {
      ...state,
      maximumNumberOfOrders,
    };
  }),

  on(OrdersFormsActions.unselectForm, (state, {}) => {
    return {
      ...state,
      selectedOrderFormId: null,
    };
  }),

  on(OrdersFormsActions.duplicateSelectedForm, (state, { dates, selectedForm, expandCard }) => {
    const newOrders = [];
    const latestOrdersIds: string[] = [];
    let entityId: string;
    dates.forEach((date) => {
      formCounter++;
      entityId = date + '/' + formCounter;
      selectedForm.values[SpecFieldNames.ID] = entityId;
      latestOrdersIds.push(entityId);
      newOrders.push({
        values: {
          ...selectedForm.values,
        },
        counter: formCounter,
        id: entityId,
        valid: selectedForm.valid,
        invalid: selectedForm.invalid,
        orderDraft: null,
        orderConfirmation: null,
        originalTimestamp: date,
        requestedTimestamp: date,
        shown: expandCard,
        shownTouched: false,
        duplicated: true,
        selectedPodAddress: selectedForm.selectedPodAddress,
      });
    });
    return adapter.addMany(newOrders, {
      ...state,
      selectedOrderFormId: entityId,
      latestDuplicatedOrdersFormsIds: latestOrdersIds,
    });
  }),

  on(OrdersFormsActions.deleteForm, (state, { formId }) => {
    return adapter.removeOne(formId, state);
  }),

  on(OrderScheduleActions.createSimulateOrderSuccess, (state, { payload }) => {
    const updatedValues = state.entities[state.selectedOrderFormId]?.values;
    return adapter.updateOne(
      {
        id: state.selectedOrderFormId,
        changes: {
          values: updatedValues,
          orderDraft: payload,
        },
      },
      state,
    );
  }),

  on(OrderScheduleActions.onlyCreateOrderSuccess, (state, { payload }) => {
    const updatedValues = state.entities[state.selectedOrderFormId].values;
    return adapter.updateOne(
      {
        id: state.selectedOrderFormId,
        changes: {
          values: updatedValues,
          orderDraft: payload,
        },
      },
      state,
    );
  }),

  on(OrderScheduleActions.clearOrderDraft, (state, {}) => {
    return adapter.updateOne(
      {
        id: state.selectedOrderFormId,
        changes: {
          orderDraft: undefined,
        },
      },
      state,
    );
  }),

  on(OrderScheduleActions.orderConfirmationRequest, (state, { payload }) => {
    const updatedValues = state.entities[state.selectedOrderFormId].values;
    return adapter.updateOne(
      {
        id: state.selectedOrderFormId,
        changes: {
          values: updatedValues,
          orderConfirmation: payload,
        },
      },
      state,
    );
  }),

  on(OrderScheduleActions.setAdditionalInfoOrderCreate, (state, { payload }) => {
    const updatedValues = state.entities[state.selectedOrderFormId].values;
    return adapter.updateOne(
      {
        id: state.selectedOrderFormId,
        changes: {
          values: updatedValues,
          ...state.entities[state.selectedOrderFormId],
          ...payload,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.updateTouchedForm, (state, { formId, isExpanded }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          shown: isExpanded,
          shownTouched: true,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.termsAndConditionsCheckbox, (state, { formId, isChecked }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          termsAndConditions: isChecked,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.resetOrderForm, (state) => {
    formCounter = -1;
    return { ...initialState };
  }),

  on(OrdersFormsActions.setBigo, (state, { formId, isBIGO }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          isBIGO,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.resetBigo, (state, { formId }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          isBIGO: null,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.setBusinessDayType, (state, { formId, businessDayType }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          businessDayType,
        },
      },
      state,
    );
  }),

  on(OrdersFormsActions.setLiabilityCheck, (state, { formId, checked }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          liabilityCheck: checked,
        },
      },
      state,
    );
  }),
  on(OrdersFormsActions.setSelectedPodAddress, (state, { formId, selectedPodAddress }) => {
    return adapter.updateOne(
      {
        id: formId,
        changes: {
          selectedPodAddress,
        },
      },
      state,
    );
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return ordersFormsReducer(state, action);
}
